import { teamStrengthColumns } from "../parts/teamStrengthColumns";
import { AdminValueEditor, leagueEditableValues, teamValueEffect } from "./AdminValueEditor";
import { useState } from "react";
import { Button, TextField, MenuItem, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { average } from "../helpers/adjustTeamStrengths";
import { leagueParameterMetrics, settingNumber } from "./LeagueWorkarea";

const fmt = (n) =>
  settingNumber(n) === null
    ? "–"
    : Number(n).toLocaleString("de-DE", { maximumFractionDigits: 3 });
export function AdminLeagueEditing({
  parameters,
  savedRows = [],
  onPreview,
  rows,
  dirty,
  saving,
  save,
  reset,
  metrics,
  sources,
  comparisons,
  previousSummary,
  previousSeason,
  previousError,
  onNormalize,
  onDetails,
  normalization,
  editor,
  teamLogos,
}) {
  const { t } = useTranslation();
  const tr = (key) => t(`help.adminLeague.${key}`);
  const [editing, setEditing] = useState(null);
  const [dimension, setDimension] = useState("total_strength");
  const [comparison, setComparison] = useState("should");
  const available = Object.keys(sources).filter((key) => sources[key]?.length);
  const selected = available.includes(comparison) ? comparison : available[0];
  const previous = leagueParameterMetrics({
    season_base_summary: previousSummary,
  }).find((r) => r.key === "actual");
  const comparisonValues = (key) => [...["actual","expected","should","market"].map(name => metrics.find(m=>m.key===name) || {key:name}), {...previous,key:"previous"}].map(m=>({
    label: m.key === "actual" ? "Real" : m.key === "previous" ? `Vorsaison ${previousSeason?.name || ""}` : m.key === "expected" ? "Expected" : m.key === "should" ? "Should" : "Markt",
    count: m.fixtures,
    value: key === "draw_factor" ? (m.drawRate==null ? null : m.drawRate*100) : key === "goal_average" ? m.goalAverage : m.homeAdvantage,
    unit: key === "draw_factor" ? " % Remisquote" : "",
  }));
  return (
    <>
      {dirty && !editing && (
        <Alert
          severity="info"
          action={
            <>
              <Button disabled={saving} onClick={reset}>
                {tr("reset")}
              </Button>
              <Button disabled={saving} onClick={save}>
                {tr("save")}
              </Button>
            </>
          }
        >
          {tr("dirty")}
        </Alert>
      )}
      <details className="al-section">
        <summary>{tr("general")}</summary>
        <div className="al-content">
          <p>Alle veränderbaren Liga-Werte · einen Wert auswählen, vergleichen und gezielt testen.</p>
          <div className="al-parameters">{leagueEditableValues.map(def=><article key={def.key}>
            <h3>{def.label}</h3><small>Aktuell gespeichert</small><strong>{fmt(parameters[def.stored])}</strong>
            <p>Team-Mittel im Editor: {fmt(average(rows,def.field))}</p>
            <Button disabled={saving || !rows.length || Boolean(editing)} onClick={()=>setEditing({definition:def})}>Bearbeiten</Button>
          </article>)}</div>
          {editing && editing.teamId==null && <AdminValueEditor key={editing.definition.key} {...{rows,dirty,saving,save,onPreview}} definition={editing.definition} savedValue={parameters[editing.definition.stored]} comparisons={comparisonValues(editing.definition.key)} onClose={()=>setEditing(null)}/>}
          <p>{tr("savedMissing")}</p>
          {previousError && <Alert severity="warning">{previousError}</Alert>}
        </div>
      </details>
      <details className="al-section">
        <summary>{tr("teams")}</summary>
        <div className="al-content">
          <div className="al-controls">
            <TextField
              select
              size="small"
              label={tr("dimension")}
              value={dimension}
              onChange={(e) => setDimension(e.target.value)}
            >
              {teamStrengthColumns.filter(c=>c.editable).map(c=><MenuItem key={c.field} value={c.field}>{c.headerName}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label={tr("compare")}
              value={selected || ""}
              onChange={(e) => setComparison(e.target.value)}
            >
              {available.map((key) => (
                <MenuItem value={key} key={key}>
                  {key === "actual"
                    ? "Real"
                    : key === "previous"
                    ? `${tr("previousSeason")} ${previousSeason?.name || ""}`
                    : key === "expected"
                    ? "Expected"
                    : key === "should"
                    ? "Should"
                    : "Markt"}
                </MenuItem>
              ))}
              {!available.length && (
                <MenuItem value="">{tr("noComparison")}</MenuItem>
              )}
            </TextField>
          </div>
          <table className="al-team-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{tr("team")}</th>
                <th>{tr("setting")}</th>
                <th>{selected || "–"}</th>
                <th title={tr("delta")}>Δ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const other = settingNumber(
                  comparisons[selected]?.get(Number(row.team_id))
                    ?.fixtureSettingValues?.[dimension]
                );
                return (
                  <tr key={row.team_id}>
                    <td>{i + 1}</td>
                    <td>
                      {teamLogos?.get(Number(row.team_id)) && (
                        <img src={teamLogos.get(Number(row.team_id))} alt="" />
                      )}
                      {row.custom_name || row.team_name}
                    </td>
                    <td>{fmt(row[dimension])}</td>
                    <td>{fmt(other)}</td>
                    <td>
                      {other === null
                        ? "–"
                        : fmt(Number(row[dimension]) - other)}
                    </td>
                    <td>
                      <Button
                        size="small"
                        disabled={saving || Boolean(editing)}
                        onClick={() => setEditing({teamId:row.team_id,definition:{key:dimension,field:dimension,label:teamStrengthColumns.find(c=>c.field===dimension)?.headerName || dimension,effect:teamValueEffect(dimension)}})}
                      >
                        {tr("edit")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {editing?.teamId!=null && <AdminValueEditor key={`${editing.teamId}:${editing.definition.key}`} {...{rows,dirty,saving,save,onPreview}} definition={editing.definition} teamId={editing.teamId} savedValue={savedRows.find(row=>Number(row.team_id)===Number(editing.teamId))?.[editing.definition.field]} comparisons={[{label:selected || "Vergleich", value:comparisons[selected]?.get(Number(editing.teamId))?.fixtureSettingValues?.[editing.definition.field]}]} onClose={()=>setEditing(null)}/>}
          <Button disabled={Boolean(editing)} onClick={()=>onDetails()}>Weitere Teamwerte im bestehenden Detail-Editor</Button>
          <details className="al-help">
            <summary>
              {tr("normalization")} · {fmt(normalization.totalDifference)}
            </summary>
            <p>
              {tr(
                normalization.goalTotalsAreNormalized
                  ? "normalized"
                  : "normalizeNeeded"
              )}
            </p>
            <p>
              For {fmt(normalization.sumForTotal)} · Against{" "}
              {fmt(normalization.sumAgainstTotal)}
            </p>
            <Button disabled={Boolean(editing)} onClick={() => onNormalize("goal_totals")}>
              {tr("normalizeGoals")}
            </Button>
            <Button disabled={Boolean(editing)} onClick={() => onNormalize("home_advantage")}>
              {tr("normalizeHome")}
            </Button>
          </details>
          {editor}
        </div>
      </details>
    </>
  );
}
export const renderAdminLeagueEditing = (props) => (
  <AdminLeagueEditing {...props} />
);
